import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  ClipboardList,
  CalendarDays,
  Users,
  Briefcase,
  Building2,
  Globe,
  BarChart3,
  UserCog,
  LogOut,
  HardHat,
  Settings,
  List,
  Upload,
  Trash2,
  Printer,
  ClipboardCheck,
  Wrench,
  Truck,
  LayoutGrid,
  Gauge,
  CalendarX2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Home } from "lucide-react";
import { isAndroidWebView, androidBottomInsetPx } from "@/lib/platform";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LangSwitch } from "@/components/LangSwitch";
import OfflineBanner, { OnlineIndicator } from "@/components/OfflineBanner";
import NotificationButton from "@/components/NotificationButton";
import OnboardingTour, { tourDoneKey } from "@/components/OnboardingTour";
import WelcomeIntro, { introSeenKey } from "@/components/WelcomeIntro";
import { PcAccessHintDialog, pcHintSeenKey, shouldShowPcHint } from "@/components/PcAccessHintDialog";
import { checkForNewVersion, forceAppUpdate, notifyIfJustUpdated } from "@/lib/appUpdate";
import { setCommessaFormat } from "@/lib/commessaCodice";
import { toast } from "sonner";

const baseItems = [
  { to: "/home", k: "home", icon: Home, mobile: true },
  { to: "/settimana", k: "settimana", icon: CalendarDays, end: true, mobile: true },
  { to: "/lista", k: "lista", icon: List, mobile: true },
  { to: "/riepilogo", k: "riepilogo", icon: BarChart3, mobile: true },
  { to: "/stampe", k: "stampe", icon: Printer, mobile: false },
  { to: "/statistiche", k: "statistiche", icon: BarChart3, mobile: false },
  { to: "/import", k: "importa", icon: Upload, mobile: false },
  { to: "/anagrafica/dipendenti", k: "dipendenti", icon: Users, mobile: false },
  { to: "/anagrafica/commesse", k: "commesse", icon: Briefcase, mobile: false },
];
const adminItems = [
  { to: "/anagrafica/clienti", k: "clienti", icon: Building2, mobile: false },
  { to: "/da-vidimare", k: "da-vidimare", icon: ClipboardCheck, mobile: false },
  { to: "/da-sistemare", k: "da-sistemare", icon: Wrench, mobile: false },
  { to: "/quadro", k: "quadro", icon: Gauge, mobile: false, perm: "can_view_dashboard" },
  { to: "/controllo-ore", k: "controllo-ore", icon: CalendarX2, mobile: false, perm: "can_view_controllo_ore" },
  { to: "/dashboard-commesse", k: "dashboard-commesse", icon: BarChart3, mobile: false, perm: "can_view_dashboard_commesse" },
  { to: "/programma", k: "programma", icon: LayoutGrid, mobile: false, perm: "can_view_risorse" },
  { to: "/risorse", k: "risorse", icon: Truck, mobile: false, perm: "can_view_risorse" },
  { to: "/utenti", k: "utenti", icon: UserCog, mobile: false },
  { to: "/cestino", k: "cestino", icon: Trash2, mobile: false },
  { to: "/impostazioni", k: "impostazioni", icon: Settings, mobile: false },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unvidCount, setUnvidCount] = useState(0);
  // Iter 48: contatore avvisi "Da sistemare" (solo admin)
  const [fixesCount, setFixesCount] = useState(0);
  // Iter 105: scadenze parco risorse (solo admin)
  const [scadCount, setScadCount] = useState(0);
  // Iter 61: tutorial guidato al primo accesso
  const [tourOpen, setTourOpen] = useState(false);
  // Iter 78: presentazione animata 60s al primissimo accesso
  const [introOpen, setIntroOpen] = useState(false);
  const [pcHintOpen, setPcHintOpen] = useState(false);
  // Iter 147: nell'app Android la barra di sistema (|||  O  <) copre la nostra barra in basso → la alziamo
  const [bottomInset] = useState(() => androidBottomInsetPx());

  // Iter 146: "Puoi usarmi anche dal PC" una volta sola, dopo intro/tour, solo da telefono
  const maybeShowPcHint = () => {
    try {
      if (!user?.email || !shouldShowPcHint() || localStorage.getItem(pcHintSeenKey(user.email))) return;
    } catch (_) { return; }
    // aspetta che non ci siano altri popup aperti (es. "Nuovi inserimenti da confermare")
    let tries = 0;
    const tick = () => {
      if (!document.querySelector('[role="dialog"]')) { setPcHintOpen(true); return; }
      if (++tries < 20) setTimeout(tick, 3000);
    };
    setTimeout(tick, 1500);
  };
  const closePcHint = () => {
    try { localStorage.setItem(pcHintSeenKey(user?.email), "1"); } catch (_) {}
    setPcHintOpen(false);
  };

  useEffect(() => {
    if (!user?.email) return;
    try {
      if (!localStorage.getItem(introSeenKey(user.email))) {
        setIntroOpen(true);
      } else if (!localStorage.getItem(tourDoneKey(user.email))) {
        setTourOpen(true);
      } else {
        maybeShowPcHint();
      }
    } catch (_) { /* noop */ }
    // eslint-disable-next-line
  }, [user?.email]);

  const closeIntro = () => {
    try { localStorage.setItem(introSeenKey(user?.email), "1"); } catch (_) {}
    setIntroOpen(false);
    try {
      if (!localStorage.getItem(tourDoneKey(user?.email))) setTourOpen(true);
      else maybeShowPcHint();
    } catch (_) {}
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Poll unread rapportini count
  const fetchUnread = async () => {
    try {
      const { data } = await api.get("/rapportini/unread-count");
      setUnreadCount(data?.count || 0);
    } catch (err) {
      // Network error or offline — silent, will retry next poll
      if (process.env.NODE_ENV === "development") {
        console.debug("[badge] unread-count poll failed:", err?.message);
      }
    }
  };

  // Poll unvidimati count (solo admin)
  const fetchUnvidimati = async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/rapportini/unvidimati-count");
      setUnvidCount(data?.count || 0);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.debug("[badge] unvidimati-count poll failed:", err?.message);
      }
    }
  };

  // Iter 48: poll contatore "Da sistemare" (solo admin)
  const fetchFixesCount = async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/admin/da-sistemare/count");
      setFixesCount(data?.totale || 0);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.debug("[badge] da-sistemare-count poll failed:", err?.message);
      }
    }
  };

  const fetchScadCount = async () => {
    if (user?.role !== "admin" && !user?.can_view_risorse) return;
    try {
      const { data } = await api.get("/risorse-scadenze", { params: { giorni: 30, count_only: true } });
      setScadCount(data?.count || 0);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    fetchUnread();
    fetchUnvidimati();
    fetchFixesCount();
    fetchScadCount();
    const t = setInterval(() => {
      fetchUnread();
      fetchUnvidimati();
      fetchFixesCount();
      fetchScadCount();
    }, 60000); // every 60s
    // Iter 48: refresh immediato quando un avviso viene sistemato
    const onFixed = () => fetchFixesCount();
    window.addEventListener("da-sistemare-changed", onFixed);
    return () => {
      clearInterval(t);
      window.removeEventListener("da-sistemare-changed", onFixed);
    };
    // eslint-disable-next-line
  }, [user?.role]);

  // Iter 44: auto-aggiornamento all'ingresso. Se la versione pubblicata è
  // diversa dal bundle in esecuzione → pulizia cache/SW + reload automatico
  // (guard in sessionStorage per evitare loop se il deploy sta propagando).
  useEffect(() => {
    // Iter 45: conferma post-aggiornamento per qualsiasi utente
    const justV = notifyIfJustUpdated();
    if (justV) {
      toast.success(`✔ App aggiornata: stai usando l'ultima versione ${justV}`, {
        duration: 6000,
      });
    }
    checkForNewVersion().then((v) => {
      if (!v) return;
      try {
        if (sessionStorage.getItem("auto_update_v") === v) return;
        sessionStorage.setItem("auto_update_v", v);
      } catch { /* storage non disponibile: procedi comunque */ }
      toast.info(`Nuova versione ${v}: aggiornamento automatico in corso…`, {
        duration: 3000,
      });
      setTimeout(() => forceAppUpdate(), 1200);
    });
    // eslint-disable-next-line
  }, []);

  // Iter 30w: applica classe globale UPPERCASE basata su settings.display_uppercase
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (cancelled) return;
        if (data?.display_uppercase) {
          document.body.classList.add("force-uppercase");
        } else {
          document.body.classList.remove("force-uppercase");
        }
      } catch (_) { /* silent */ }
      // Iter 55: carica il formato commessa configurato (maschera + validazioni)
      try {
        const { data: fmt } = await api.get("/commesse/formato");
        if (!cancelled) setCommessaFormat(fmt);
      } catch (_) { /* default EG-XX-XXX */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);


  // Refresh unvidimati count quando si naviga via /da-vidimare (admin completa vidima)
  useEffect(() => {
    fetchUnvidimati();
    fetchFixesCount();
    // eslint-disable-next-line
  }, [location.pathname]);

  // Mark as seen when user visits /lista
  useEffect(() => {
    if (location.pathname === "/lista" && unreadCount > 0) {
      api.post("/rapportini/mark-seen")
        .then(() => setUnreadCount(0))
        .catch((err) => console.warn("[badge] mark-seen failed:", err.message));
    }
  }, [location.pathname]);

  const isViewer = user?.role === "viewer";
  const isCapoCantiere = user?.role === "capo_cantiere" || user?.role === "capo_officina";
  const isAdmin = user?.role === "admin";

  // Capo cantiere: vede Settimana, Lista, Riepilogo + Commesse (per crearle).
  // NO Stampe, Importa, Dipendenti, Utenti, Impostazioni.
  const visibleBase = isViewer
    ? baseItems.filter(
        (it) =>
          it.to !== "/import" &&
          (it.to !== "/stampe" || user?.can_view_stampe_ore !== false)
      )
    : isCapoCantiere
    ? baseItems.filter((it) =>
        ["/home", "/settimana", "/lista", "/riepilogo", "/anagrafica/commesse"].includes(it.to)
      )
    : baseItems;
  const items = isAdmin
    ? [
        ...baseItems,
        ...adminItems,
        // Iter 64: pannello SaaS solo per gli admin della piattaforma (no tenant)
        ...(!user?.company_id && !user?.is_demo
          ? [{ to: "/aziende", k: "aziende", icon: Globe, mobile: false }]
          : []),
      ].filter(
        (it) => it.to !== "/impostazioni" || user?.can_manage_settings !== false
      )
    : [
        ...visibleBase,
        // Iter 109: Risorse/Programma per utenti flaggati (sola lettura o modifica)
        ...adminItems.filter((it) => it.perm && user?.[it.perm]),
      ];
  const mobileItems = (isViewer || isCapoCantiere)
    ? items.filter((it) => it.mobile).slice(0, 4)
    : items.filter((it) => it.mobile).slice(0, 4).concat(
        [{ to: "/import", k: "importa", icon: Upload }]
      );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="no-print sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1700px] mx-auto flex items-center justify-between gap-x-3 gap-y-1 flex-wrap px-4 md:px-6 min-h-16 py-1.5">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-[#0044FF] text-white grid place-items-center">
              <HardHat className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-base tracking-tight">
                IL-Report
              </div>
              <div className="text-xs text-slate-500 hidden sm:block">
                {t("subtitle")}
              </div>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-1.5 flex-wrap justify-center flex-1 min-w-0">
            {items.map((item) => {
              const Icon = item.icon;
              const isLista = item.to === "/lista";
              const isDaVidimare = item.to === "/da-vidimare";
              const isDaSistemare = item.to === "/da-sistemare";
              const isRisorse = item.to === "/risorse";
              const badgeValue = isLista
                ? unreadCount
                : isDaVidimare
                ? unvidCount
                : isDaSistemare
                ? fixesCount
                : isRisorse
                ? scadCount
                : 0;
              const badgeColor = isDaVidimare || isRisorse
                ? "bg-amber-500"
                : isDaSistemare
                ? "bg-orange-500"
                : "bg-red-600";
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.k}`}
                  className={({ isActive }) =>
                    `relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all duration-150 ${
                      isActive
                        ? "bg-[#0044FF] text-white shadow-md shadow-blue-500/30"
                        : "text-slate-600 border border-transparent hover:border-slate-200 hover:bg-white hover:text-[#0044FF] hover:shadow-sm hover:-translate-y-[1px]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {Icon && isActive && (
                        <Icon className="h-4 w-4 shrink-0" strokeWidth={2.4} />
                      )}
                      {t(`nav.${item.k}`)}
                      {badgeValue > 0 && (
                        <span
                          data-testid={`${isDaVidimare ? "unvid" : isDaSistemare ? "fixes" : isRisorse ? "scad" : "unread"}-badge-desktop`}
                          className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full ${badgeColor} text-white text-[10px] font-black flex items-center justify-center`}
                        >
                          {badgeValue > 99 ? "99+" : badgeValue}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-1.5 md:gap-2">
            <NotificationButton />
            <OnlineIndicator className="hidden md:inline-flex" />
            <NavLink
              to="/profilo"
              className="text-right block max-w-[96px] md:max-w-none group hover:opacity-80 transition"
              data-testid="profile-link"
              title="Vai al profilo"
            >
              <div className="text-xs md:text-sm font-semibold leading-tight group-hover:underline truncate">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wide truncate">
                {t(
                  ["admin", "viewer", "capo_cantiere", "capo_officina"].includes(user?.role)
                    ? `roles.${user.role}`
                    : "roles.user"
                )}
              </div>
            </NavLink>
            <LangSwitch />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmLogout(true)}
              data-testid="logout-button"
              className="h-10"
            >
              <LogOut className="h-4 w-4 mr-1 md:mr-2" />
              <span className="text-xs md:text-sm">{t("logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <AlertDialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <AlertDialogContent data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("logoutConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("logoutConfirmSub")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel-btn">{t("logoutCancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout} data-testid="logout-confirm-btn">
              {t("logout")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <main className="flex-1 pb-24 lg:pb-8" style={bottomInset ? { paddingBottom: `calc(6rem + ${bottomInset}px)` } : undefined}>
        <OfflineBanner />
        {user?.trial_days_left != null && !user?.trial_expired && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800"
            data-testid="trial-banner"
          >
            <b>{t("trial.label")}</b>: {user.trial_days_left}{" "}
            {user.trial_days_left === 1 ? t("trial.dayLeft") : t("trial.daysLeft")} ·{" "}
            {user.company_name}
            {user?.role === "admin" && !isAndroidWebView() && (
              <button
                type="button"
                onClick={() => navigate("/abbonamento")}
                data-testid="trial-banner-subscribe-link"
                className="ml-2 underline font-bold hover:text-amber-950"
              >
                {t("trial.subscribe")}
              </button>
            )}
          </div>
        )}
        {user?.trial_expired && (
          <div
            className="bg-red-50 border-b border-red-200 px-4 py-2 text-center text-sm text-red-800"
            data-testid="trial-expired-banner"
          >
            <b>{user.plan && user.plan !== "trial" ? t("expired.sub") : t("expired.trial")}</b>
            {t("expired.readonly")}
            {user?.role === "admin" && !isAndroidWebView() && (
              <button
                type="button"
                onClick={() => navigate("/abbonamento")}
                data-testid="expired-banner-subscribe-link"
                className="ml-2 underline font-bold hover:text-red-950"
              >
                {t("expired.activate")}
              </button>
            )}
          </div>
        )}
        <WelcomeIntro open={introOpen} onClose={closeIntro} />
        <OnboardingTour open={tourOpen} onClose={() => { setTourOpen(false); maybeShowPcHint(); }} />
        <PcAccessHintDialog open={pcHintOpen} onClose={closePcHint} />
        {user?.is_demo && (
          <div
            className="no-print bg-amber-100 border-b border-amber-300 text-amber-900 text-center text-xs sm:text-sm font-bold px-3 py-2"
            data-testid="demo-banner"
          >
            {t("demoBanner")}
          </div>
        )}
        <Outlet />
      </main>

      <nav className="no-print lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200" style={bottomInset ? { paddingBottom: `${bottomInset}px` } : undefined} data-testid="bottom-nav" data-bottom-inset={bottomInset}>
        <div
          className="grid max-w-3xl mx-auto"
          style={{ gridTemplateColumns: `repeat(${mobileItems.length}, minmax(0, 1fr))` }}
        >
          {mobileItems.map((item) => {
            const Icon = item.icon;
            const isLista = item.to === "/lista";
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`bottom-nav-${item.k}`}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center py-2 text-[11px] font-semibold gap-0.5 ${
                    isActive ? "text-[#0044FF]" : "text-slate-600"
                  }`
                }
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {isLista && unreadCount > 0 && (
                    <span
                      data-testid="unread-badge-mobile"
                      className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center"
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </div>
                {t(`nav.${item.k}`)}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
